/* ============================================================================
   FORMATO DE REPORTE DEL OPERADOR — FRM-SMS-002 Rev. 00

   Transcrito del Anexo 2 del `MAN-SIG-SMS-001 Manual SMS Rev. 01`: el
   «Reporte de Identificación de Peligros y Ocurrencias» que ANDES usa en papel
   (buzones en bases de operación) y en su formulario virtual.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ SE COPIA EL FORMATO EN VEZ DE DISEÑAR UNO MEJOR

   Un reporte capturado con campos distintos de los del formulario aprobado
   **no es el mismo registro**. Un auditor que compare el papel con la pantalla
   tiene que ver lo mismo, y el personal que ya sabe llenar el formulario no
   tiene que aprender otro.

   Si algún campo de acá parece de más o mal ubicado, la respuesta no es
   cambiarlo: es proponer una revisión del FRM-SMS-002 ante la Gerencia SIG.

   TRES COSAS DEL FORMULARIO QUE NO SON DETALLES

   1. **«NO PUNITIVO».** El formulario lo dice en negrita, dos veces. Es la
      base de la cultura justa: sin esa garantía, los reportes que importan no
      se escriben. El texto se muestra tal cual, no resumido.

   2. **Nombre y correo son OPCIONALES.** Están marcados así en el formulario.
      Un reporte sin nombre es un reporte válido, no uno incompleto.

   3. **Numeración correlativa.** El formulario en papel trae «Nº 0000»
      preimpreso. Un número correlativo permite detectar que falta uno — que es
      justamente lo que un auditor busca.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** De dónde salió este formato. Viaja en cada registro y en los informes. */
export const FORMATO = {
  codigo: "FRM-SMS-002",
  revision: "Rev. 00",
  fecha: "Ene-2015",
  titulo: "Reporte de Identificación de Peligros y Ocurrencias",
  manual: "MAN-SIG-SMS-001 Manual SMS Rev. 01, Anexo 2",
  operador: "ANDES",
  correo: "sig@losandessac.com",
};

/**
 * El aviso del formulario, palabra por palabra.
 *
 * No se resume ni se reescribe: es la garantía sobre la que se apoya todo el
 * programa de reportes, y el personal la reconoce.
 */
export const AVISO_NO_PUNITIVO =
  "La información que usted consigne aquí será utilizada únicamente con ánimo "
  + "investigativo y confidencial, NO PUNITIVO de quien reporta ni de los presuntos "
  + "implicados, a menos que conlleve declaraciones violatorias.";

export const AVISO_USO =
  "La información será útil para efectos de retroalimentar al Sistema Integrado de "
  + "Gestión sobre la gestión de riesgos operacionales, laborales, satisfacción del "
  + "cliente y la minimización de impactos ambientales.";

/** Los tres tipos que ofrece el formulario, en su orden. */
export const TIPOS_REPORTE = [
  ["peligro", "Reporte de Identificación de Peligro",
    "Algo que todavía no pasó pero puede pasar."],
  ["ocurrencia", "Reporte de Ocurrencia",
    "Algo que ya pasó."],
  ["mejora", "Reporte de Mejora",
    "Una oportunidad de hacer algo mejor."],
];

/**
 * Sección II del formulario: a qué sistema de gestión corresponde.
 *
 * El formulario virtual agrega «Security (AVSEC) y Mercancías Peligrosas», que
 * el de papel no tiene. Se incluyen los cinco: el operador ya lo usa así.
 */
export const SISTEMAS = [
  ["sms", "Seguridad Operacional (SMS)"],
  ["calidad", "Calidad (ISO 9001)"],
  ["sso", "Seguridad y Salud Ocupacional (OHSAS 18001)"],
  ["ambiente", "Medio Ambiente (ISO 14001)"],
  ["avsec", "Security (AVSEC) y Mercancías Peligrosas"],
];

/**
 * Las preguntas de la sección III, tal como las imprime el formulario.
 *
 * Se muestran como guía al redactar. Sin ellas, un reporte suele decir qué
 * pasó y nada más — y sin los factores contribuyentes no se puede analizar.
 */
export const GUIA_DESCRIPCION = [
  "¿Qué pasó?",
  "¿Cómo fue identificado el evento?",
  "¿Qué acciones se tomaron?",
  "¿Por qué cree que ocurrió el evento?",
  "¿Qué factores contribuyeron al evento?",
];

import {
  validarDimensiones, requisitosDelRol, ROLES_REPORTANTE,
} from "./sistemaIntegrado.js";

const texto = (v) => (typeof v === "string" ? v.trim() : "");
const num = (v) => (Number.isFinite(v) ? v : null);

/**
 * Número correlativo con el formato del papel: cuatro dígitos.
 *
 * El formulario impreso trae «Nº 0000». Que la numeración sea correlativa es
 * lo que permite detectar un faltante — un identificador al azar no.
 */
export function numeroCorrelativo(n) {
  const v = num(n);
  if (v === null || v < 0) return null;
  return `N° ${String(Math.floor(v)).padStart(4, "0")}`;
}

/**
 * El siguiente número de la serie.
 *
 * Se calcula sobre los existentes y NO sobre un contador aparte: un contador
 * que se desincronice repetiría números, y dos reportes con el mismo número es
 * exactamente lo que un auditor no puede aceptar.
 */
export function siguienteNumero(registros = []) {
  const lista = Array.isArray(registros) ? registros : [];
  const usados = lista
    .map((r) => num(r?.numero))
    .filter((n) => n !== null);
  return usados.length === 0 ? 1 : Math.max(...usados) + 1;
}

/**
 * Detecta huecos en la numeración.
 *
 * Un faltante no significa necesariamente que se perdió algo —puede haberse
 * anulado un formulario— pero **el auditor va a preguntar**, y hay que poder
 * responder.
 */
export function huecosDeNumeracion(registros = []) {
  const lista = Array.isArray(registros) ? registros : [];
  const usados = lista.map((r) => num(r?.numero)).filter((n) => n !== null).sort((a, b) => a - b);
  if (usados.length === 0) return { huecos: [], repetidos: [], desde: null, hasta: null };

  const huecos = [];
  const repetidos = [];

  for (let i = usados[0]; i <= usados[usados.length - 1]; i += 1) {
    const cuantos = usados.filter((n) => n === i).length;
    if (cuantos === 0) huecos.push(i);
    if (cuantos > 1) repetidos.push(i);
  }

  return {
    huecos,
    repetidos: [...new Set(repetidos)],
    desde: usados[0],
    hasta: usados[usados.length - 1],
  };
}

/**
 * Valida un reporte según el formulario.
 *
 * Lo que el formulario marca como opcional **es opcional**: exigirlo sería
 * cambiar el formato aprobado, y además romper la garantía de anonimato.
 */
export function validarReporte(datos = {}) {
  const errores = [];
  const avisos = [];

  if (!datos || typeof datos !== "object") {
    return { valido: false, errores: ["No se cargó ningún dato."], avisos };
  }

  const tipo = texto(datos.tipoReporte);
  if (!TIPOS_REPORTE.some(([c]) => c === tipo)) {
    errores.push(`Marca el tipo de reporte: ${TIPOS_REPORTE.map(([, e]) => e).join(", ")}.`);
  }

  /* DIMENSIONES, en plural.

     El formulario en papel obliga a marcar UNA. Un derrame de combustible en
     plataforma es a la vez seguridad operacional, ambiental y ocupacional:
     obligar a elegir hace que el evento se registre una vez y se pierda para
     las otras dos auditorías.

     Se acepta `sistema` en singular por compatibilidad con lo ya cargado. */
  const entrada = Array.isArray(datos.dimensiones)
    ? datos.dimensiones
    : (texto(datos.sistema) ? [texto(datos.sistema)] : []);

  const dim = validarDimensiones(entrada);
  errores.push(...dim.errores);
  avisos.push(...dim.avisos);

  const confidencial = datos?.confidencial === true;
  const reportante = texto(datos?.reportante);

  /* EL ROL de quien reporta. Reporta cualquiera: tripulación, mantenimiento,
     tierra, despacho, y también CLIENTE y PASAJEROS. */
  const rol = texto(datos.rolReportante);
  const req = requisitosDelRol(rol);

  if (rol && !ROLES_REPORTANTE.some(([c]) => c === rol)) {
    errores.push(`Rol de reportante desconocido: ${rol}.`);
  }

  /* A un EXTERNO no se le exige identificación. Un pasajero no tiene código de
     área, puede no querer dar su nombre, y su reporte no puede quedar trabado
     porque falte un dato de empleado — el manual dice que reporta cualquiera. */
  if (req.interno && !confidencial && !reportante) {
    errores.push("Un reporte identificado necesita quién lo reporta. "
      + "Si prefieres no dar tu nombre, marcalo como confidencial.");
  }

  if (rol && req.derivarAlSig) avisos.push(req.nota);

  /* Sección I. La fecha del evento es lo que ancla el reporte en el tiempo:
     sin ella no se puede correlacionar con vuelos ni con otros reportes. */
  if (!texto(datos.fechaEvento)) errores.push("Falta la fecha del evento.");

  const descripcion = texto(datos.descripcion);
  if (descripcion.length < 20) {
    /* El formulario pide describir «de forma clara y precisa». Un renglón no
       alcanza para analizar nada. */
    errores.push("La descripción tiene que contar qué pasó con el detalle suficiente "
      + "para analizarlo (al menos 20 caracteres).");
  }

  /* Nombre y correo son OPCIONALES según el formulario. Si no están, el
     reporte es anónimo — y eso es válido, no un error. */
  if (!texto(datos.nombre)) {
    avisos.push("Reporte sin nombre: queda anónimo. Es válido según el formulario.");
  }

  if (!texto(datos.zonaOperacion)) {
    avisos.push("Sin zona de operación: cuesta más ubicar el contexto del evento.");
  }
  if (!texto(datos.aeronave)) {
    avisos.push("Sin aeronave: si el evento involucró una, conviene indicarla.");
  }
  if (!texto(datos.recomendaciones)) {
    /* La sección IV existe porque quien vio el problema suele saber cómo
       evitarlo. Que falte no invalida el reporte. */
    avisos.push("Sin recomendaciones (sección IV): quien vio el problema suele "
      + "saber cómo evitarlo.");
  }

  /* Se devuelve lo ya normalizado: recalcularlo en `armarReporte` duplicaría
     la lógica, y dos copias de la misma regla se separan tarde o temprano. */
  return {
    valido: errores.length === 0,
    errores,
    avisos,
    dimensiones: dim.dimensiones,
    rol: rol || null,
    externo: Boolean(rol) && !req.interno,
  };
}

/**
 * Arma el reporte con la estructura del formulario.
 *
 * Devuelve los campos en las cuatro secciones del papel, para que un auditor
 * que compare vea lo mismo.
 */
export function armarReporte(datos = {}, { numero = null } = {}) {
  const { valido, errores, avisos, dimensiones, rol, externo } = validarReporte(datos);
  if (!valido) return { reporte: null, errores, avisos };

  const anonimo = !texto(datos.nombre);


  return {
    reporte: {
      formato: FORMATO.codigo,
      revision: FORMATO.revision,
      numero: num(numero),
      numeroTexto: numeroCorrelativo(numero),

      tipoReporte: texto(datos.tipoReporte),
      /* Las dimensiones marcadas, en el orden declarado para que los informes
         salgan siempre iguales. */
      dimensiones,
      /* Se conserva el singular por compatibilidad: es la columna, o la
         primera marcada si el evento no toca seguridad operacional. */
      sistema: dimensiones.includes("sms") ? "sms" : (dimensiones[0] || null),
      rolReportante: rol,
      reportanteExterno: externo,

      /* Sección I — Información general */
      fechaEvento: texto(datos.fechaEvento),
      fechaReporte: texto(datos.fechaReporte) || new Date().toISOString().slice(0, 10),
      zonaOperacion: texto(datos.zonaOperacion) || null,
      aeronave: texto(datos.aeronave) || null,
      rtv: texto(datos.rtv) || null,
      elaboradoPor: texto(datos.elaboradoPor) || null,
      nombre: texto(datos.nombre) || null,
      email: texto(datos.email) || null,
      anonimo,

      /* Sección III y IV */
      descripcion: texto(datos.descripcion),
      recomendaciones: texto(datos.recomendaciones) || null,

      procedencia: {
        formato: `${FORMATO.codigo} ${FORMATO.revision}`,
        manual: FORMATO.manual,
        via: texto(datos.via) || "aplicación",
      },
    },
    errores: [],
    avisos,
  };
}

/**
 * Las vías de reporte que el manual reconoce (§4.2.1).
 *
 * HeliCalc es una vía más, no un reemplazo: el buzón de papel y el correo
 * siguen existiendo, y un reporte que llegó por ahí se carga acá indicando por
 * dónde vino.
 */
export const VIAS = [
  ["buzon", "Buzón en base de operación (formulario en papel)"],
  ["virtual", "Formulario virtual"],
  ["correo", `Correo a ${FORMATO.correo}`],
  ["telefono", "Vía telefónica, transcrito por el SIG"],
  ["aplicacion", "HeliCalc"],
];
